import { Component, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DeprecatedConfirmationDialogComponent, DeprecatedModalFormComponent } from '@app/components';
import { BulkSelectSurveyParticipantsDialogComponent } from '@app/components/dialogs/bulk-select-survey-participants-dialog/bulk-select-survey-participants-dialog.component';
import { AnalyticEvents, FeatureFlag } from '@app/enums';
import { selectedEmployeesHaveChanged } from '@app/functions';
import { RoutingStateService } from '@app/modules/common/services/routing-state.service';
import { PerformanceExportService } from '@app/modules/performance/services/performance-export-service';
import { AnalyticService } from '@app/services';
import { AuthService } from '@app/services/auth.service';
import { FeatureService } from '@app/services/feature.service';
import { NotifyService } from '@app/services/notify.service';
import { ShowView } from '@common/views';
import { Employee } from '@models/employee/employee.model';
import { Survey } from '@models/employee/performance/survey.model';
import { SurveyResponse } from '@models/performance/survey-response.model';
import { SurveyDisplayComponent } from '@performance/components/survey-display/survey-display.component';
import { Status } from '@performance/enums/status.enum';
import { PerformanceResolver } from '@performance/performance.resolver';

@Component({
    templateUrl: './survey-show.template.html',
    styleUrls: ['./survey-show.style.scss'],
})
export class SurveyShowView extends ShowView {
    @ViewChild('confirmDelete', { static: true }) confirmDelete: DeprecatedConfirmationDialogComponent;
    @ViewChild('surveyParticipantsSelection', { static: false })
    surveyParticipantsSelection: BulkSelectSurveyParticipantsDialogComponent;
    @ViewChild('surveyDisplayComponent') surveyDisplayComponent: SurveyDisplayComponent;
    @ViewChild('surveyDetailsModal') surveyDetailsModal: DeprecatedModalFormComponent;

    isLoading = false;
    survey: Survey = this.route.snapshot.data.models.get('survey');
    Status = Status;
    editSurvey: string[] = Survey.permission.edit;
    settings: any;
    isCreator = this.auth.employee.id === this.survey.createdBy;
    isAdmin = this.auth.isAdmin();
    backRoute: string;

    hasPerformanceReviewImprovementsFeatureFlag = false;

    constructor(
        public auth: AuthService,
        private route: ActivatedRoute,
        private featureService: FeatureService,
        private router: Router,
        private analyticService: AnalyticService,
        private notify: NotifyService,
        private resolver: PerformanceResolver,
        private performanceExportService: PerformanceExportService,
        private routingState: RoutingStateService
    ) {
        super();

        if (!this.survey.userResponse) {
            this.survey.userResponse = new SurveyResponse();
        }
    }

    async ngOnInit(): Promise<void> {
        const [performanceReviewImprovements] = await Promise.all([
            this.featureService.has(FeatureFlag.performanceReviewImprovements),
        ]);

        this.hasPerformanceReviewImprovementsFeatureFlag = performanceReviewImprovements;

        // Do not redirect back to the survey results (causing back button loop)
        this.backRoute = this.routingState.firstPreviousUrlNotMatching(/(\/aggregated|raw)-data/);
    }

    showEdit(): void {
        this.surveyDetailsModal.show();
    }

    reResolve(): void {
        this.isLoading = true;
        this.resolver.resolve(this.route.snapshot, this.router.routerState.snapshot).then((models) => {
            this.survey = <Survey>models.get('survey');
            if (!this.survey.userResponse) {
                this.survey.userResponse = new SurveyResponse();
            }
            this.isLoading = false;
        });
    }

    onSave(): void {
        this.isLoading = true;
        this.survey
            .save()
            .then(() => {
                this.reResolve();
                this.notify.success('surveys.show.surveySaved');

                this.analyticService.trackEvent(AnalyticEvents.SurveysEditSurveySave);
            })
            .catch((err) => {
                this.notify.error(err);
                this.isLoading = false;
            });
    }

    onCancel(): void {}

    onDelete(): void {
        this.confirmDelete.show().then((onDelete) => {
            if (onDelete) {
                this.isLoading = true;
                this.survey
                    .delete()
                    .then(() => {
                        this.router.navigate(['./surveys', 'center']);

                        this.analyticService.trackEvent(AnalyticEvents.SurveysDeleteSurveyConfirm);
                    })
                    .catch((err) => {
                        this.notify.error(err);
                        this.isLoading = false;
                    });
            }
        });
    }

    onAssign(): void {
        const initialEmployees = Array.from(this.survey.participants);

        this.surveyParticipantsSelection.show<Employee[] | null>(initialEmployees).then((selectedEmployees) => {
            if (!Array.isArray(selectedEmployees)) {
                selectedEmployees = initialEmployees;
            }

            if (selectedEmployeesHaveChanged(initialEmployees, selectedEmployees)) {
                this.survey.sync('participants', selectedEmployees);
                this.onSave();

                this.analyticService.trackEvent(AnalyticEvents.SurveysAssignParticipants);
            }
        });
    }

    remind(): void {
        this.isLoading = true;
        this.survey
            .remind()
            .then(() => {
                this.notify.success('surveys.show.remindersSent');
                this.isLoading = false;

                this.analyticService.trackEvent(AnalyticEvents.SurveysRemindParticipants);
            })
            .catch((err) => {
                this.isLoading = false;
                this.notify.error(err);
            });
    }

    forceComplete(): void {
        this.isLoading = true;
        this.survey
            .force()
            .then(() => {
                this.reResolve();

                this.analyticService.trackEvent(AnalyticEvents.SurveysForceComplete);
            })
            .catch((err) => {
                this.isLoading = false;
                this.notify.error(err);
            });
    }

    results(): void {
        this.analyticService.trackEvent(AnalyticEvents.SurveysViewResults);
        this.router.navigate(['./aggregated-data'], { relativeTo: this.route });
    }

    onResponseSave(response: SurveyResponse): void {
        // stop the issue where submitted surveys are autosaved
        if (response.autoSaved && response.submittedAt) {
            return;
        }

        if (!response.autoSaved) {
            this.isLoading = true;
        }

        response
            .param('survey', this.survey.id)
            .save()
            .then((r) => {
                this.survey.userResponse.id = r.id;
                if (!response.autoSaved) {
                    this.notify.success('surveys.show.surveyUpdated');
                    this.reResolve();
                    return;
                }
                this.isLoading = false;
            })
            .catch((err) => {
                this.isLoading = false;
                this.notify.error(err);
            });
    }

    launch(): void {
        this.surveyDisplayComponent.launch();
    }

    onExport(): void {
        this.performanceExportService.exportSurvey(this.survey);

        this.analyticService.trackEvent(AnalyticEvents.SurveysExportResults);
    }
}
